// Drip Campaigns — V2 Roadmap (PRD)
//
// PM-facing read-only document. Content lives as structured data in this file
// (single source of truth) so it stays in version control and renders cleanly
// without a markdown parser. Update sections in V2_SECTIONS to evolve the doc.

import { Link } from "wouter";
import {
  ChevronLeft, Sparkles, ListChecks, CalendarClock, CalendarPlus,
  MessageSquareReply, Inbox, Compass, Lightbulb,
} from "lucide-react";

type Effort = "S (≤1d)" | "M (1–3d)" | "L (3–7d)" | "XL (1–2w)";
type Priority = "P0 — must-have" | "P1 — high" | "P2 — nice-to-have" | "Stretch";

interface Section {
  id: string;
  number: number;
  title: string;
  icon: any;
  oneLiner: string;
  why: string;
  what: string[];
  effort: Effort;
  priority: Priority;
  dependsOn: string[];
  acceptance: string[];
  risks: string[];
}

const V2_SECTIONS: Section[] = [
  {
    id: "ai-brief-parser",
    number: 1,
    title: "AI Brief Parser — paste plain English, get drafts",
    icon: Sparkles,
    oneLiner:
      "A textarea on the wizard that turns a normal email or memo into one or many fully-drafted campaigns.",
    why:
      "Today the team fills 6 form fields per campaign and can build only one at a time. The actual brief usually lives in a Slack thread or an email to leadership (e.g. the launch plan email to Pranav). That brief contains everything we need; we should let people paste it and have the AI extract the rest.",
    what: [
      "Add a large 'Paste a plain-English brief' textarea on the New Campaign wizard, alongside the existing field-by-field form.",
      "On submit, the AI returns a structured proposal: number of distinct campaigns, audience for each, pacing mode (relative vs absolute), email count, draft subjects.",
      "User confirms, edits, or rejects the proposal before any DB writes.",
      "On confirm, each campaign is created as a draft with all 10 (or N) emails generated, ready for review.",
    ],
    effort: "M (1–3d)",
    priority: "P0 — must-have",
    dependsOn: ["variable-email-count", "absolute-date-pacing"],
    acceptance: [
      "Paste the 'launch plan to Pranav' email → AI proposes 2 campaigns (Investor Drip 3 emails on absolute dates, Community Launch 2 emails on absolute dates).",
      "User confirms → 2 draft campaigns appear on the campaigns list within 30s.",
      "All emails reference real Favcy Select features and use uploaded screenshot keys (no hallucinations).",
    ],
    risks: [
      "AI may misclassify pacing mode or audience. Mitigated by the always-required user confirmation step before write.",
      "Long briefs may exceed the prompt budget. Mitigated by truncating to first ~3000 tokens and warning the user.",
    ],
  },
  {
    id: "variable-email-count",
    number: 2,
    title: "Variable email count (drop the hardcoded '10')",
    icon: ListChecks,
    oneLiner:
      "Each campaign declares its own email count instead of being forced to exactly 10.",
    why:
      "The current schema, cron, generator prompt, and UI all assume exactly 10 emails per campaign. The Pranav launch plan needs a 3-email phase and a 2-email phase. Forcing 10 here would dilute the message and confuse recipients.",
    what: [
      "Add `emailCount` (int, 1–20) to the campaigns table.",
      "Update the generator prompt to produce N emails instead of always 10.",
      "Update cron to stop advancing once a sub reaches `emailCount` on its campaign.",
      "Replace every 'X/10 emails' label in the admin UI with 'X/N'.",
    ],
    effort: "M (1–3d)",
    priority: "P0 — must-have",
    dependsOn: [],
    acceptance: [
      "Wizard accepts a number from 1 to 20.",
      "A 3-email campaign generates exactly 3 templates and the cron stops sending after Day 3.",
      "Existing 10-email campaigns (Default Investor Drip, E2E Test Batch) keep working unchanged.",
    ],
    risks: [
      "Template UI assumes 10 in many places; need a thorough grep & replace. Test the editor's 'next/prev day' nav at boundaries (1 and N).",
    ],
  },
  {
    id: "absolute-date-pacing",
    number: 3,
    title: "Absolute-date pacing (calendar dates, not 'D+N')",
    icon: CalendarClock,
    oneLiner:
      "Each campaign picks 'relative days from enrollment' OR 'specific calendar dates'.",
    why:
      "Today everything is relative day numbers (D1, D3, D5…). Launches like the 2 May investor demo need to send on a wall-clock date in IST, not 'whenever the recipient was enrolled + 5 days'. Both modes are legitimate and should coexist.",
    what: [
      "Add `pacingMode` enum to campaigns ('relative_days' | 'absolute_dates').",
      "Add `sendDates` (array of timestamps with timezone) to campaign templates when pacing is absolute.",
      "Cron tick uses sendDates when present; falls back to relative offsets otherwise.",
      "Wizard surface: a pair of radio buttons; the AI parser auto-detects which mode the brief implies and asks before committing.",
    ],
    effort: "M (1–3d)",
    priority: "P0 — must-have",
    dependsOn: [],
    acceptance: [
      "An absolute-date campaign with [29 Apr, 30 Apr, 1 May 9 AM IST] sends each email on the exact wall-clock date in IST.",
      "Default Investor Drip remains relative-day and unchanged.",
      "DST / timezone-edge cases tested: a 12 noon IST send arrives as 12 noon IST regardless of recipient location.",
    ],
    risks: [
      "Timezone bugs are easy to ship by accident — every send timestamp must be stored as UTC + a source TZ string, not naive local time.",
      "Backfill: existing templates have no sendDates; they default to relative mode.",
    ],
  },
  {
    id: "calendar-invites",
    number: 4,
    title: "Calendar invites — .ics attachment + 'Add to Calendar' buttons",
    icon: CalendarPlus,
    oneLiner:
      "Drip emails for event-anchored campaigns include a working calendar invite recipients can add in one click.",
    why:
      "Launches like the 2 May investor demo currently say 'Saturday 12 noon' in plain text — recipients have to manually enter it into their calendar. That's a meaningful drop-off in attendance. Real event tools (Eventbrite, Luma) attach an .ics AND show 'Add to Google Calendar' buttons. We should too.",
    what: [
      "Per-campaign event field (title, start, end, location/URL, description). Optional — only shown when pacing is absolute and the brief mentions an event.",
      "On send, generate an .ics file and attach to every outbound email (Resend supports attachments).",
      "Render 'Add to Google Calendar', 'Add to Outlook', and 'Add to Apple Calendar' buttons in the email body as a fallback for webmail users.",
      "Editable from the campaign detail page; preview the rendered buttons in the existing live-preview iframe.",
    ],
    effort: "M (1–3d)",
    priority: "P1 — high",
    dependsOn: ["absolute-date-pacing"],
    acceptance: [
      "An invite to the 2 May 12 noon IST demo arrives with a clickable .ics — opening it in Outlook / Apple Mail prefills the event with correct title, time, and TZ.",
      "Gmail web users see a styled 'Add to Google Calendar' button that opens a pre-filled new event tab.",
      "Timezone displayed correctly for recipients in IST and PT (no '12 AM PDT' bugs).",
    ],
    risks: [
      ".ics MIME-type quirks across providers — Outlook is strict; test before shipping.",
      "RSVP responses (Outlook 'Accept') generate a reply email back. Without reply tracking (#5), those go to a black hole.",
    ],
  },
  {
    id: "reply-tracking",
    number: 5,
    title: "Reply tracking — capture & thread investor responses",
    icon: MessageSquareReply,
    oneLiner:
      "When a recipient replies to a drip email, we capture it, thread it to the right campaign+day, and surface it in the admin UI.",
    why:
      "Today replies hit a noreply mailbox nobody reads. We're losing the highest-signal engagement event a drip can produce: someone actually wrote back. Marketing should see 'Day 2 of Investor Drip got 3 replies — click to read' next to the send stats.",
    what: [
      "Send each outbound with a unique reply-to: `replies+<sub_id>+<day>@favcyselect.in` (subaddressing).",
      "Set up Resend inbound webhook + an MX record on `favcyselect.in`.",
      "New `campaign_replies` table: replyId, campaignId, subscriptionId, day, fromEmail, subject, bodyText, bodyHtml, receivedAt.",
      "Strip quoted reply chains and signatures using a small library so the displayed text is just the new content.",
      "Detect bounces / out-of-office / auto-reply and tag separately so they don't pollute engagement metrics.",
      "Admin UI: per-campaign 'Replies' tab + per-recipient drawer showing the thread.",
    ],
    effort: "L (3–7d)",
    priority: "P1 — high",
    dependsOn: [],
    acceptance: [
      "An investor replies to a Day 2 email; the reply appears in the campaign detail view within 30 seconds, threaded under that recipient's Day 2 send.",
      "Out-of-office auto-replies are tagged 'auto' and excluded from reply counts.",
      "Bounce notifications are routed to a separate 'Bounces' view and the recipient's status flips to 'bounced'.",
    ],
    risks: [
      "DNS access — needs whoever owns favcyselect.in to add an MX record. That's the bottleneck more than the code.",
      "Spam on the inbound channel — gate the webhook on Resend signature + drop unsigned mail.",
    ],
  },
  {
    id: "inbound-email-channel",
    number: 6,
    title: "Inbound brief channel — forward an email, get a draft",
    icon: Inbox,
    oneLiner:
      "Marketing forwards a brief to `campaigns@favcyselect.in` and a draft campaign appears in the admin within 30 seconds.",
    why:
      "Even the textarea requires logging into the admin. Most briefs originate as emails or Slack DMs. Letting people forward straight to a magic address kills the friction entirely — no app-switching, no copy-paste.",
    what: [
      "Inbound Resend address `campaigns@favcyselect.in` reusing the MX setup from #5.",
      "Webhook handler: verify sender is on an allowlist of team emails, strip signature/reply-chain, run the body through the same AI parser as the textarea path.",
      "Created drafts get a Slack notification with a deep link to review.",
    ],
    effort: "M (1–3d)",
    priority: "Stretch",
    dependsOn: ["ai-brief-parser", "reply-tracking"],
    acceptance: [
      "Forward the Pranav launch-plan email to campaigns@favcyselect.in → 2 draft campaigns appear within 30s, plus a Slack ping.",
      "Mail from someone outside the allowlist is rejected silently with no draft created.",
    ],
    risks: [
      "Signature parsing is hard. We accept some draft noise for v1 — admins clean up before approving.",
      "If senders forward an entire thread, the AI may parse the wrong message. Mitigation: pick the *most recent* message body only.",
    ],
  },
];

const PRIORITY_STYLE: Record<Priority, string> = {
  "P0 — must-have": "bg-red-500/10 text-red-700 border-red-500/30",
  "P1 — high": "bg-amber-500/10 text-amber-700 border-amber-500/30",
  "P2 — nice-to-have": "bg-blue-500/10 text-blue-700 border-blue-500/30",
  "Stretch": "bg-muted text-muted-foreground border-border",
};

export default function AdminCampaignsRoadmap() {
  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <Link href="/admin/campaigns">
          <a className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 mb-3">
            <ChevronLeft className="w-3 h-3" /> Back to campaigns
          </a>
        </Link>
        <h1 className="text-3xl font-serif tracking-tight">Drip Campaigns — V2 Roadmap</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Where this tool is going next. Read this if you want to know what's possible, what's coming, and which gaps in v1 are intentional vs. on the build list.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-border bg-muted/40">
            <Compass className="w-3 h-3" /> Document type: PRD
          </span>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-border bg-muted/40">
            Audience: PM, founder, marketing lead
          </span>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-border bg-muted/40">
            Status: shaped, not yet sequenced for build
          </span>
        </div>
      </div>

      {/* Vision */}
      <section className="rounded-lg border border-[#FF5C00]/30 bg-[#FF5C00]/5 p-5 space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-[#FF5C00]" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#FF5C00]">Vision</h2>
        </div>
        <p className="text-sm leading-relaxed">
          A marketing teammate writes a normal email describing what they want to send — to whom, when, and around what event — and the system produces a fully-drafted, calendar-aware drip campaign ready for one-click approval. No form-filling, no day-by-day prompt engineering, no separate calendar tool. The product owns the loop from <em>"here's the plan"</em> to <em>"recipients have it on their calendar and replies are flowing back to us."</em>
        </p>
      </section>

      {/* Today's gaps */}
      <section className="rounded-lg border border-border p-5 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Today's gaps (what V1 doesn't do)</h2>
        <ul className="text-sm space-y-1.5 list-disc pl-5">
          <li>Every campaign is hardcoded to <strong>exactly 10 emails</strong>. Real launches need 2–5.</li>
          <li>Pacing is <strong>relative day numbers from enrollment only</strong>. Event-anchored launches need calendar dates.</li>
          <li>One brief = one wizard form. <strong>Multi-phase plans</strong> require running the wizard multiple times.</li>
          <li>Mentions of events ("Saturday 12 noon") <strong>have no calendar invite</strong> — recipients add manually or forget.</li>
          <li>Replies from investors <strong>go to a noreply black hole</strong>. Highest-signal engagement event is lost.</li>
          <li>Briefs must be <strong>typed/pasted into the admin UI</strong>. No way to forward an email and have a draft appear.</li>
        </ul>
      </section>

      {/* Sections */}
      <section className="space-y-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Proposed enhancements</h2>
        {V2_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.id} id={s.id} className="rounded-lg border border-border overflow-hidden scroll-mt-6">
              <header className="px-5 py-4 border-b border-border bg-muted/30">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-md bg-[#FF5C00]/10 text-[#FF5C00] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">V2.{s.number}</p>
                      <h3 className="text-base font-semibold mt-0.5">{s.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{s.oneLiner}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded border ${PRIORITY_STYLE[s.priority]}`}>{s.priority}</span>
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded border border-border bg-background text-muted-foreground">
                      Effort: {s.effort}
                    </span>
                  </div>
                </div>
              </header>
              <div className="p-5 space-y-4 text-sm">
                <Block label="Why">
                  <p className="leading-relaxed">{s.why}</p>
                </Block>

                <Block label="What we'd build">
                  <ul className="list-disc pl-5 space-y-1">
                    {s.what.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </Block>

                {s.dependsOn.length > 0 && (
                  <Block label="Depends on">
                    <p className="text-xs text-muted-foreground">
                      {s.dependsOn.map((d, i) => {
                        const dep = V2_SECTIONS.find((v) => v.id === d);
                        return (
                          <span key={d}>
                            {i > 0 && ", "}
                            <a href={`#${d}`} className="text-[#FF5C00] hover:underline">V2.{dep?.number} {dep?.title.split(" — ")[0]}</a>
                          </span>
                        );
                      })}
                    </p>
                  </Block>
                )}

                <Block label="Done when">
                  <ul className="list-disc pl-5 space-y-1">
                    {s.acceptance.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </Block>

                <Block label="Risks / open questions">
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    {s.risks.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </Block>
              </div>
            </article>
          );
        })}
      </section>

      {/* Suggested sequence */}
      <section className="rounded-lg border border-border p-5 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Suggested build sequence</h2>
        <ol className="text-sm space-y-1.5 list-decimal pl-5">
          <li><strong>V2.2 Variable email count</strong> — schema + cron + UI relabel. Unblocks everything else.</li>
          <li><strong>V2.3 Absolute-date pacing</strong> — schema + cron timezone work. Unblocks calendar invites.</li>
          <li><strong>V2.1 AI brief parser</strong> — the headline UX win, only safe to ship after the two above.</li>
          <li><strong>V2.4 Calendar invites</strong> — ships alongside the first absolute-date launch.</li>
          <li><strong>V2.5 Reply tracking</strong> — separate track, gated on DNS access for an MX record.</li>
          <li><strong>V2.6 Inbound channel</strong> — only if the team actually asks for it after using V2.1 for a few weeks.</li>
        </ol>
      </section>

      {/* Out of scope */}
      <section className="rounded-lg border border-border p-5 space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Explicitly out of scope (for now)</h2>
        <ul className="text-sm space-y-1.5 list-disc pl-5 text-muted-foreground">
          <li>A/B testing of subject lines or CTAs across recipients.</li>
          <li>Per-recipient personalization beyond {`{{name}}`} substitution.</li>
          <li>SMS / WhatsApp dripping. Email only for now.</li>
          <li>Native event hosting (Zoom/Meet integration). We just link out.</li>
          <li>Marketing analytics beyond send/open/click. Use the existing Resend dashboard.</li>
        </ul>
      </section>

      <p className="text-xs text-muted-foreground italic">
        This document lives in <code className="text-[10px] bg-muted px-1 py-0.5 rounded">artifacts/venture-os/src/pages/admin/admin-campaigns-roadmap.tsx</code>. Edit it directly to keep the roadmap in version control.
      </p>
    </div>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{label}</p>
      {children}
    </div>
  );
}
